import { yupResolver } from '@hookform/resolvers/yup';
import { DRAWER_DEFAULT_LENGTH_IN_CM, DRAWER_DEFAULT_WIDTH_IN_CM } from 'helpers/configurator/configuratorConstants';
import { useShopsysForm } from 'hooks/forms/UseShopsysForm';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ConfiguratorDrawerSizesFormType } from 'types/form';
import { number, object } from 'yup';

export const useConfiguratorDrawerSizesForm = (): [
    UseFormReturn<ConfiguratorDrawerSizesFormType>,
    ConfiguratorDrawerSizesFormType,
] => {
    const { t } = useTranslation();

    const resolver = yupResolver(
        object().shape({
            width: number()
                .transform((_, value) => parseFloat(value.toString().replace(/,/g, '.')))
                .required(t('Please enter width'))
                .typeError(t('Please enter only numbers'))
                .max(200, t('Maximal width is {{ amount }}', { amount: 200 }))
                .min(1, t('Minimal width is {{ amount }}', { amount: 1 })),
            length: number()
                .transform((_, value) => parseFloat(value.toString().replace(/,/g, '.')))
                .required(t('Please enter length'))
                .typeError(t('Please enter only numbers'))
                .max(200, t('Maximal length is {{ amount }}', { amount: 200 }))
                .min(1, t('Minimal length is {{ amount }}', { amount: 1 })),
        }),
    );

    const defaultValues: ConfiguratorDrawerSizesFormType = {
        width: DRAWER_DEFAULT_WIDTH_IN_CM,
        length: DRAWER_DEFAULT_LENGTH_IN_CM,
    };

    return [useShopsysForm(resolver, defaultValues), defaultValues];
};

type ConfiguratorDrawerSizesFormMetaType = {
    formName: string;
    fields: {
        [key in keyof ConfiguratorDrawerSizesFormType]: {
            name: key;
            label: string;
            errorMessage: string | undefined;
        };
    };
};

export const useConfiguratorDrawerSizesFormMeta = (
    formProviderMethods: UseFormReturn<ConfiguratorDrawerSizesFormType>,
): ConfiguratorDrawerSizesFormMetaType => {
    const { t } = useTranslation();

    return useMemo(
        () => ({
            formName: 'configurator-drawer-sizes-form',
            fields: {
                width: {
                    name: 'width' as const,
                    label: t('Width (in cm)'),
                    errorMessage: formProviderMethods.formState.errors.width?.message,
                },
                length: {
                    name: 'length' as const,
                    label: t('Length (in cm)'),
                    errorMessage: formProviderMethods.formState.errors.length?.message,
                },
            },
        }),
        [formProviderMethods.formState.errors, t],
    );
};
